import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { Button } from "./ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { cn } from "../lib/utils";

/**
 * Grouped ticker combobox with search (yfinance-compatible symbols).
 * Covers crypto, actions, indices/ETFs, devises, matières premières, obligations.
 */
export const TICKER_GROUPS = [
  {
    label: "Monnaies & Stablecoins",
    items: [
      { symbol: "EUR", name: "Euro (EUR)" },
      { symbol: "USD", name: "Dollar US (USD)" },
      { symbol: "CHF", name: "Franc suisse (CHF)" },
      { symbol: "USDT", name: "Tether USD" },
      { symbol: "USDC", name: "USD Coin" },
      { symbol: "DAI", name: "Dai Stablecoin" },
      { symbol: "EURC", name: "Euro Coin (EURC)" },
      { symbol: "BUSD", name: "Binance USD" },
      { symbol: "TUSD", name: "TrueUSD" },
    ],
  },
  {
    label: "Crypto",
    items: [
      { symbol: "BTC-USD", name: "Bitcoin" },
      { symbol: "ETH-USD", name: "Ethereum" },
      { symbol: "SOL-USD", name: "Solana" },
      { symbol: "BNB-USD", name: "BNB" },
      { symbol: "XRP-USD", name: "XRP" },
      { symbol: "ADA-USD", name: "Cardano" },
      { symbol: "AVAX-USD", name: "Avalanche" },
      { symbol: "DOGE-USD", name: "Dogecoin" },
      { symbol: "LINK-USD", name: "Chainlink" },
      { symbol: "DOT-USD", name: "Polkadot" },
      { symbol: "MATIC-USD", name: "Polygon" },
      { symbol: "ATOM-USD", name: "Cosmos" },
      { symbol: "LTC-USD", name: "Litecoin" },
    ],
  },
  {
    label: "Actions US",
    items: [
      { symbol: "AAPL", name: "Apple" },
      { symbol: "MSFT", name: "Microsoft" },
      { symbol: "GOOGL", name: "Alphabet (Google)" },
      { symbol: "AMZN", name: "Amazon" },
      { symbol: "META", name: "Meta" },
      { symbol: "NVDA", name: "NVIDIA" },
      { symbol: "TSLA", name: "Tesla" },
      { symbol: "NFLX", name: "Netflix" },
      { symbol: "AMD", name: "AMD" },
      { symbol: "INTC", name: "Intel" },
      { symbol: "COIN", name: "Coinbase" },
      { symbol: "MSTR", name: "Strategy (MicroStrategy)" },
      { symbol: "CRCL", name: "Circle" },
      { symbol: "JPM", name: "JPMorgan Chase" },
      { symbol: "V", name: "Visa" },
      { symbol: "MA", name: "Mastercard" },
      { symbol: "BRK-B", name: "Berkshire Hathaway B" },
    ],
  },
  {
    label: "Indices & ETF",
    items: [
      { symbol: "SPY", name: "S&P 500 ETF" },
      { symbol: "QQQ", name: "Nasdaq 100 ETF" },
      { symbol: "DIA", name: "Dow Jones ETF" },
      { symbol: "IWM", name: "Russell 2000 ETF" },
      { symbol: "VTI", name: "Total US Market ETF" },
      { symbol: "VOO", name: "Vanguard S&P 500" },
      { symbol: "^GSPC", name: "S&P 500 index" },
      { symbol: "^IXIC", name: "Nasdaq Composite" },
      { symbol: "^DJI", name: "Dow Jones industrial" },
      { symbol: "^FCHI", name: "CAC 40" },
      { symbol: "^GDAXI", name: "DAX" },
      { symbol: "^FTSE", name: "FTSE 100" },
      { symbol: "^N225", name: "Nikkei 225" },
    ],
  },
  {
    label: "Matières premières",
    items: [
      { symbol: "GC=F", name: "Or (Gold Futures)" },
      { symbol: "SI=F", name: "Argent (Silver Futures)" },
      { symbol: "PL=F", name: "Platine" },
      { symbol: "HG=F", name: "Cuivre" },
      { symbol: "CL=F", name: "Pétrole WTI" },
      { symbol: "BZ=F", name: "Pétrole Brent" },
      { symbol: "NG=F", name: "Gaz naturel" },
      { symbol: "URA", name: "Uranium (URA ETF)" },
      { symbol: "ZC=F", name: "Maïs" },
      { symbol: "ZW=F", name: "Blé" },
    ],
  },
  {
    label: "Devises (Forex)",
    items: [
      { symbol: "EURUSD=X", name: "EUR / USD" },
      { symbol: "GBPUSD=X", name: "GBP / USD" },
      { symbol: "USDJPY=X", name: "USD / JPY" },
      { symbol: "USDCHF=X", name: "USD / CHF" },
      { symbol: "AUDUSD=X", name: "AUD / USD" },
      { symbol: "USDCAD=X", name: "USD / CAD" },
      { symbol: "EURGBP=X", name: "EUR / GBP" },
      { symbol: "DX-Y.NYB", name: "DXY (dollar index)" },
    ],
  },
  {
    label: "Obligations",
    items: [
      { symbol: "TLT", name: "US 20Y+ Treasury ETF" },
      { symbol: "IEF", name: "US 7-10Y Treasury ETF" },
      { symbol: "SHY", name: "US 1-3Y Treasury ETF" },
      { symbol: "^TNX", name: "US 10Y yield" },
      { symbol: "^TYX", name: "US 30Y yield" },
    ],
  },
];

const FLAT = TICKER_GROUPS.flatMap((g) => g.items.map((i) => ({ ...i, group: g.label })));

export function findTicker(symbol) {
  return FLAT.find((t) => t.symbol === symbol) || null;
}

// Filtre de la liste fixe : nom ou ticker, sans tenir compte des accents ni des majuscules
const norm = (x) => (x || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
const matches = (t, q) => !q || norm(`${t.name} ${t.symbol}`).includes(norm(q));

/**
 * remote : ajoute une recherche Yahoo Finance (admin) sous la liste fixe, pour trouver n'importe quel actif
 * coté (ex. « Nintendo » → 7974.T à Tokyo, NTDOY aux États-Unis). onChange(symbole, infos) reçoit alors
 * { name, exchange, asset_type } en second argument.
 */
export default function TickerCombobox({ value, onChange, placeholder = "Choisir un actif…", testid = "ticker-combobox", remote = false }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState(null); // infos du dernier actif choisi dans la recherche Yahoo
  const current = useMemo(() => findTicker(value) || (picked && picked.symbol === value ? picked : null), [value, picked]);
  const q = query.trim();
  const { data: found = [], isFetching } = useQuery({
    queryKey: ["ticker-search", q.toLowerCase()],
    queryFn: async () => (await api.get("/admin/tickers/search", { params: { q } })).data,
    enabled: remote && open && q.length >= 2,
    staleTime: 60 * 60 * 1000,
  });
  const remoteItems = found.filter((r) => !findTicker(r.symbol));
  const groups = TICKER_GROUPS.map((g) => ({ ...g, items: g.items.filter((t) => matches(t, q)) })).filter((g) => g.items.length);
  const nothing = !groups.length && !remoteItems.length && !(remote && isFetching);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between bg-white/[0.03] border-white/10 h-10 font-normal"
          data-testid={testid}
        >
          <span className="truncate flex items-center gap-2">
            {current ? (
              <>
                <span className="text-zinc-100">{current.name}</span>
                <span className="text-[10px] text-zinc-500 font-mono-num uppercase">{current.symbol}</span>
              </>
            ) : value ? (
              <span className="text-zinc-100 font-mono-num">{value}</span>
            ) : (
              <span className="text-zinc-500 flex items-center gap-2">
                <Search className="w-3.5 h-3.5" /> {placeholder}
              </span>
            )}
          </span>
          <ChevronsUpDown className="w-4 h-4 text-zinc-500 shrink-0 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0 bg-card border-white/10"
        align="start"
        onWheel={(e) => e.stopPropagation()}
        onPointerDownOutside={(e) => {
          // Avoid parent Dialog interpreting it as outside click
          if (e.target && e.target.closest("[cmdk-list]")) e.preventDefault();
        }}
      >
        <Command className="bg-transparent" shouldFilter={false}>
          <CommandInput
            placeholder={remote ? "Chercher un actif (nom ou ticker), ex. Nintendo…" : "Chercher un actif (nom ou ticker)…"}
            value={query}
            onValueChange={setQuery}
            data-testid={`${testid}-input`}
          />
          <CommandList className="max-h-[320px] overflow-y-auto overscroll-contain" data-testid={`${testid}-list`}>
            {nothing && <CommandEmpty>Aucun résultat.</CommandEmpty>}
            {groups.map((g) => (
              <CommandGroup key={g.label} heading={g.label}>
                {g.items.map((t) => (
                  <CommandItem
                    key={t.symbol}
                    value={`${t.name} ${t.symbol}`}
                    onSelect={() => {
                      onChange?.(t.symbol, { name: t.name, group: g.label });
                      setOpen(false);
                    }}
                    className="cursor-pointer aria-selected:bg-emerald-500/10 aria-selected:text-emerald-200"
                    data-testid={`${testid}-item-${t.symbol}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="truncate">{t.name}</div>
                      <div className="text-[10px] text-zinc-500 font-mono-num">{t.symbol}</div>
                    </div>
                    <Check className={cn("w-4 h-4", value === t.symbol ? "opacity-100 text-emerald-400" : "opacity-0")} />
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
            {remote && q.length >= 2 && (
              <CommandGroup heading={isFetching ? "Yahoo Finance · recherche…" : "Yahoo Finance"}>
                {remoteItems.map((r) => (
                  <CommandItem
                    key={`yf-${r.symbol}`}
                    value={`yf ${r.symbol}`}
                    onSelect={() => {
                      setPicked(r);
                      onChange?.(r.symbol, r);
                      setOpen(false);
                    }}
                    className="cursor-pointer aria-selected:bg-emerald-500/10 aria-selected:text-emerald-200"
                    data-testid={`${testid}-yf-${r.symbol}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="truncate">{r.name}</div>
                      <div className="text-[10px] text-zinc-500 font-mono-num">
                        {r.symbol}{r.exchange ? ` · ${r.exchange}` : ""}{r.type ? ` · ${r.type}` : ""}
                      </div>
                    </div>
                    <Check className={cn("w-4 h-4", value === r.symbol ? "opacity-100 text-emerald-400" : "opacity-0")} />
                  </CommandItem>
                ))}
                {!isFetching && remoteItems.length === 0 && (
                  <div className="px-3 py-2 text-xs text-zinc-500">Aucun autre actif trouvé sur Yahoo Finance.</div>
                )}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
